<template>
  <v-app class="bg-lightBlue">
    <v-container>
      <v-row class="justify-center">
        <v-col class="col-12 col-md-6">
          <v-card class="pa-6 text-center">
            <h2 class="primary--text mb-4">Pagamento PIX</h2>

            <div v-if="qrCode" class="qr-code-container mb-4">
              <img :src="qrCodeImage" alt="QR Code PIX" class="qr-code-image" />
            </div>

            <div v-if="timeRemaining > 0" class="timer-container mb-4">
              <v-icon color="warning" class="mr-2">access_time</v-icon>
              <span class="timer-text">
                Expira em:
                <strong>{{ formattedTime }}</strong>
              </span>
            </div>
            <div v-else class="timer-expired mb-4">
              <v-icon color="error" class="mr-2">error</v-icon>
              <span class="error--text font-weight-bold">QR Code expirado</span>
            </div>

            <div v-if="pixKey" class="pix-key-container mb-4">
              <p class="gray--text mb-2">Chave PIX:</p>
              <div class="d-flex align-center justify-center pix-key-box">
                <span class="pix-key-text primary--text">{{ pixKey }}</span>
                <v-btn icon small color="secondary" @click="copyPixKey" class="ml-2">
                  <v-icon small>content_copy</v-icon>
                </v-btn>
              </div>
            </div>

            <div class="value-container mb-6">
              <p class="gray--text mb-1">Valor da consulta:</p>
              <p class="secondary--text text-h5 font-weight-bold">
                {{ formattedValue }}
              </p>
            </div>

            <v-btn large color="primary" @click="goToConsultation" class="mb-4">
              Voltar para minha consulta
            </v-btn>

            <p class="caption gray--text mt-4">
              Após o pagamento, aguarde a confirmação automática.
              <br />
              Você pode acompanhar o status no detalhe da consulta.
            </p>
          </v-card>
        </v-col>
      </v-row>

      <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
        {{ snackbarMessage }}
        <v-btn dark text @click="snackbar = false">Fechar</v-btn>
      </v-snackbar>
    </v-container>
  </v-app>
</template>

<script>
import getLanguage from '../../utils/getLanguage';
import Api from '../../services/httpClient';
import { checkPaymentStatus } from '../../services/patient';

export default {
  name: 'PixQRCode',
  data() {
    return {
      qrCode: '',
      pixKey: '',
      value: 0,
      consultationId: '',
      patientId: '',
      expiresAt: '',
      timeRemaining: 1800,
      timerInterval: null,
      pollingInterval: null,
      paymentIntentId: '',
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      lang: '',
    };
  },
  computed: {
    qrCodeImage() {
      if (!this.qrCode) return '';
      if (this.qrCode.startsWith('http') || this.qrCode.startsWith('data:')) {
        return this.qrCode;
      }
      return `data:image/png;base64,${this.qrCode}`;
    },
    formattedTime() {
      const minutes = Math.floor(this.timeRemaining / 60);
      const seconds = this.timeRemaining % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
    formattedValue() {
      const currency = this.lang === 'pt-BR' ? 'BRL' : 'EUR';
      const locale = this.lang === 'pt-BR' ? 'pt-BR' : 'pt-PT';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(this.value);
    },
  },
  async created() {
    this.lang = getLanguage(window.location.href);
    this.loadQueryParams();
    await this.checkInitialStatus();
    this.startTimer();
    this.startPaymentPolling();
  },
  beforeDestroy() {
    this.clearTimer();
    this.clearPolling();
  },
  methods: {
    loadQueryParams() {
      const { qrCode, pixKey, value, consultationId, expiresAt, patientId, paymentIntentId } =
        this.$route.query;

      this.qrCode = qrCode || '';
      this.pixKey = pixKey || '';
      this.value = parseFloat(value) || 0;
      this.consultationId = consultationId || '';
      this.patientId = patientId || localStorage.getItem('user-id') || '';
      this.paymentIntentId = paymentIntentId || '';
      this.expiresAt = expiresAt || '';

      if (this.expiresAt) {
        // expires_at é Unix timestamp em segundos
        const expirationTime = parseInt(this.expiresAt, 10) * 1000;
        const now = Date.now();
        const remainingMs = expirationTime - now;

        if (remainingMs > 0) {
          this.timeRemaining = Math.floor(remainingMs / 1000);
        } else {
          this.timeRemaining = 0;
        }
      }
    },
    startTimer() {
      this.timerInterval = setInterval(() => {
        if (this.timeRemaining > 0) {
          this.timeRemaining--;
        } else {
          this.clearTimer();
        }
      }, 1000);
    },
    clearTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },
    copyPixKey() {
      if (navigator.clipboard && this.pixKey) {
        navigator.clipboard
          .writeText(this.pixKey)
          .then(() => {
            this.showSnackbar('Chave PIX copiada com sucesso!', 'success');
          })
          .catch(() => {
            this.fallbackCopy(this.pixKey);
          });
      } else {
        this.fallbackCopy(this.pixKey);
      }
    },
    fallbackCopy(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand('copy');
        this.showSnackbar('Chave PIX copiada com sucesso!', 'success');
      } catch (err) {
        this.showSnackbar('Erro ao copiar a chave PIX', 'error');
      }

      document.body.removeChild(textArea);
    },
    showSnackbar(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    goToConsultation() {
      if (this.consultationId) {
        this.$router.replace(`/paciente/detalhe-consulta/${this.consultationId}`);
      } else {
        this.$router.replace('/paciente/schedule');
      }
    },
    async checkInitialStatus() {
      if (!this.consultationId) return;
      if (!this.patientId && !this.paymentIntentId) return;
      try {
        if (this.patientId) {
          const response = await Api.get(
            `/patients/${this.patientId}/appointments/${this.consultationId}/`,
          );
          if (response.data.status_payment === 'APPROVED') {
            this.$router.replace(`/paciente/detalhe-consulta/${this.consultationId}`);
          }
        } else {
          const response = await checkPaymentStatus(this.paymentIntentId);
          if (
            response.data.status === 'succeeded' ||
            response.data.status_payment === 'APPROVED'
          ) {
            this.$router.replace(`/paciente/detalhe-consulta/${this.consultationId}`);
          }
        }
      } catch (error) {
        console.log('Erro ao verificar status inicial:', error);
      }
    },
    startPaymentPolling() {
      if (!this.consultationId) return;
      if (!this.patientId && !this.paymentIntentId) return;
      this.pollingInterval = setInterval(() => {
        this.pollStatus();
      }, 5000);
    },
    async pollStatus() {
      try {
        if (this.patientId && this.consultationId) {
          const response = await Api.get(
            `/patients/${this.patientId}/appointments/${this.consultationId}/`,
          );
          if (response.data.status_payment === 'APPROVED') {
            this.clearPolling();
            this.$router.replace(`/paciente/detalhe-consulta/${this.consultationId}`);
          }
        } else if (this.paymentIntentId) {
          const response = await checkPaymentStatus(this.paymentIntentId);
          if (
            response.data.status === 'succeeded' ||
            response.data.status_payment === 'APPROVED'
          ) {
            this.clearPolling();
            this.$router.replace(`/paciente/detalhe-consulta/${this.consultationId}`);
          }
        }
      } catch (error) {
        console.log('Erro ao verificar status do pagamento:', error);
      }
    },
    clearPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
  },
};
</script>

<style scoped>
.qr-code-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.qr-code-image {
  max-width: 250px;
  max-height: 250px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px;
  background: white;
}

.timer-container {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff3e0;
  padding: 12px 24px;
  border-radius: 8px;
}

.timer-text {
  font-size: 1.1rem;
  color: #f57c00;
}

.timer-expired {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffebee;
  padding: 12px 24px;
  border-radius: 8px;
}

.pix-key-container {
  background: #f7f7f7;
  padding: 16px;
  border-radius: 8px;
}

.pix-key-box {
  background: white;
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.pix-key-text {
  font-family: monospace;
  font-size: 0.95rem;
  word-break: break-all;
}

.value-container {
  background: #e8f5e9;
  padding: 16px;
  border-radius: 8px;
}
</style>
